import * as React from 'react';
import { useMemo } from 'react';
import { MetricCard } from '@app/react-common/components/metric-card/MetricCard';
import { useCheckoutGroupMetrics, CheckoutGroupMetricsResponse } from '../../hooks/useCheckoutGroupMetrics';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { getSignOfCurrency } from '@utils/localization-utils';
import useShop from '@app/react-common/hooks/useShop';

interface CheckoutGroupMetricsProps {
  checkoutGroupId: number;
  isLive: boolean;
  startDate?: string;
  endDate?: string;
}

interface MetricConfig {
  key: string;
  title: string;
  getValue: (metrics: CheckoutGroupMetricsResponse) => string | number;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
  showOnlyWhenLive?: boolean;
}

// Metrics configuration schema factory
const getMetricsConfig = (currencySymbol: string): MetricConfig[] => [
  {
    key: 'checkouts',
    title: 'Checkouts',
    getValue: (metrics) => metrics.checkouts,
  },
  {
    key: 'percentageOfAllCheckouts',
    title: '% of All Checkouts',
    getValue: (metrics) => metrics.percentageOfAllCheckouts.toFixed(2),
    suffix: '%',
    tooltip: '% of PDQ shipping checkouts exposed to this group',
    showOnlyWhenLive: true,
  },
  {
    key: 'orders',
    title: 'Orders',
    getValue: (metrics) => metrics.orders,
  },
  {
    key: 'checkoutConversionRate',
    title: 'Checkout Conversion Rate',
    getValue: (metrics) => metrics.checkoutConversionRate.toFixed(2),
    suffix: '%',
    tooltip: '% of checkout sessions that result in completed orders',
  },
  {
    key: 'revenue',
    title: 'Revenue',
    getValue: (metrics) => metrics.revenue,
    prefix: currencySymbol,
    tooltip: 'Total monetary value generated from both product sales and shipping fees',
  },
  {
    key: 'avgRevenuePerCheckout',
    title: 'Avg. Revenue per Checkout',
    getValue: (metrics) => metrics.avgRevenuePerCheckout,
    prefix: currencySymbol,
    tooltip: 'The average total revenue (including shipping) generated per checkout session',
  },
  {
    key: 'averageOrderValue',
    title: 'Avg. Order Value',
    getValue: (metrics) => metrics.averageOrderValue,
    prefix: currencySymbol,
    tooltip: 'The average dollar amount spent per completed order',
  },
  {
    key: 'avgShippingRevenue',
    title: 'Avg. Shipping Revenue',
    getValue: (metrics) => metrics.avgShippingRevenue,
    prefix: currencySymbol,
    tooltip: 'The average shipping fees collected per order',
  },
];

export const CheckoutGroupMetrics: React.FC<CheckoutGroupMetricsProps> = ({
  checkoutGroupId,
  isLive,
  startDate,
  endDate,
}) => {
  const { metrics, loading: metricsLoading, status } = useCheckoutGroupMetrics({ checkoutGroupId, startDate, endDate });
  const shop = useShop();
  const currencySymbol = getSignOfCurrency(shop?.metadata?.currency) || '$';

  // Show loading state if either metrics or shop data is loading
  const loading = metricsLoading || !shop;

  // Filter metrics based on isLive status
  const visibleMetrics = useMemo(
    () => getMetricsConfig(currencySymbol).filter((config) => !config.showOnlyWhenLive || isLive),
    [isLive, currencySymbol],
  );

  // Show banner for no data (204 response)
  if (status === 'no_data') {
    return (
      <div className="checkout-group-metrics">
        <WarningBanner
          title="Pay attention"
          content="This checkout group wasn't shown in any checkout sessions during the selected time range."
          hideCloseButton={true}
          variant="attention"
        />
      </div>
    );
  }

  // Show banner for error (500 or other error responses)
  if (status === 'error') {
    return (
      <div className="checkout-group-metrics">
        <WarningBanner
          title="Pay attention"
          content="Data couldn't be loaded. Refresh the page to try again."
          hideCloseButton={true}
          variant="attention"
        />
      </div>
    );
  }

  return (
    <div className="checkout-group-metrics">
      <div className="checkout-group-metrics__grid">
        {visibleMetrics.map((config) => (
          <MetricCard
            key={config.key}
            title={config.title}
            value={metrics ? config.getValue(metrics) : 0}
            prefix={config.prefix}
            suffix={config.suffix}
            tooltip={config.tooltip}
            loading={loading}
          />
        ))}
      </div>
    </div>
  );
};
